import {
  Box,
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  SelectChangeEvent,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchBar from "../../components/SearchBar";
import { useEffect, useMemo, useState } from "react";
import AddIcon from "@mui/icons-material/Add";
import React from "react";
import SwitchToggle from "../../components/SwitchToggle";
import RewardAuditLogTable from "../../features/manageRewards/rewardAuditLogTable";
import {
  useDeletePromotionMutation,
  useLazyGetPromotionListQuery,
  useUpdatePromotionMutation,
} from "../../services/promotions";
import Pagination from "../../components/Panigation";
import DeleteConfirmationModal from "../../components/DeleteModal";
import {
  REWARD_CATEGORY,
  REWARD_TYPE,
  REWARDSPROMOTIONS_TYPE,
} from "../../constants/enums";
import { isValidInput } from "../../utils/validations";
import usePermission from "../../hooks/userAccess";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `simple-tab-${index}`,
    "aria-controls": `simple-tabpanel-${index}`,
  };
}

const ManageRewards = () => {
  const navigate = useNavigate();

  const { isAdd, isEdit, isDelete, isView } = usePermission({
    label: "Rewards & Offers Management",
    page_permission: "isView",
  });

  const [triggerList, { data: listRes, isFetching }] =
    useLazyGetPromotionListQuery();
  const [deletePromotion, { isLoading: isDeleting }] =
    useDeletePromotionMutation();
  const [updatePromotion, { isLoading: isUpdating }] =
    useUpdatePromotionMutation();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>("");
  const [valueTabs, setValueTabs] = useState(0);
  const [page, setPage] = useState(1);
  const [openDelete, setOpenDelete] = useState(false);
  const [activeId, setActiveId] = useState("");
  const [limit, setLimit] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [Status, setStatus] = React.useState("default");
  const [Sort, setSort] = React.useState("default");
  const [TypeFilter, setTypeFilter] = React.useState<string>("default");

  const toggleDeleteModal = () => {
    setOpenDelete(!openDelete);
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  useEffect(() => {
    const params: any = {
      page,
      limit,
      search: debouncedSearchTerm,
      status: Status,
    };
    if (Sort !== "default") {
      params.sort = Sort; // 'asc' | 'desc'
    }
    if (TypeFilter !== "default") {
      params.type = Number(TypeFilter);
    } else {
      params.onlyRewardAndPromotion = true;
    }
    triggerList(params);
  }, [triggerList, page, limit, debouncedSearchTerm, Status, Sort, TypeFilter]);

  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  const totalItems =
    typeof (listRes as any)?.data?.count === "number"
      ? (listRes as any).data.count
      : Array.isArray((listRes as any)?.data?.RewardPromotions)
      ? (listRes as any).data.RewardPromotions.length
      : 0;

  const totalPages = Math.max(1, Math.ceil((totalItems || 0) / (limit || 10)));

  const rewards = useMemo(() => {
    setTotalCount(listRes?.data?.count || 1);
    const items: any[] = Array.isArray(listRes?.data?.RewardPromotions)
      ? (listRes?.data?.RewardPromotions as any[])
      : [];
    return items.map((item: any) => ({
      ...item,
      id: item?._id || item?.id,
    }));
  }, [listRes]);

  const handleDelete = async () => {
    try {
      await deletePromotion({ id: activeId }).unwrap();
      toggleDeleteModal();

      // If deleting the last item on the current page and not on page 1, go to previous page
      const currentPage = page;
      const shouldGoToPreviousPage = currentPage > 1 && rewards.length === 1;
      const targetPage = shouldGoToPreviousPage ? currentPage - 1 : currentPage;

      const params: any = {
        page: targetPage,
        limit,
        search: debouncedSearchTerm,
        status: Status,
      };
      if (Sort !== "default") {
        params.sort = Sort;
      }
      if (TypeFilter !== "default") {
        params.type = Number(TypeFilter);
      } else {
        params.onlyRewardAndPromotion = true;
      }
      triggerList(params);

      // Update page state if we're going to previous page
      if (shouldGoToPreviousPage) {
        setPage(targetPage);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleToggle = async (promo: any, nextChecked: boolean) => {
    const id = promo?._id || promo?.id;
    if (!id) return;
    const nextIsBlocked = nextChecked;
    try {
      await updatePromotion({ body: { isActive: nextIsBlocked, id } }).unwrap();
      const params: any = {
        page,
        limit,
        search: debouncedSearchTerm,
        status: Status,
      };
      if (Sort !== "default") {
        params.sort = Sort;
      }
      if (TypeFilter !== "default") {
        params.type = Number(TypeFilter);
      } else {
        params.onlyRewardAndPromotion = true;
      }
      triggerList(params);
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <>
      <div className="main_title">
        <h1>Rewards & Offers Management</h1>
        <p>
          <button onClick={() => navigate("/dashboard")}>Dashboard</button> -
          Rewards & Offers Management
        </p>
      </div>

      <div className="table_header">
        <div className="left_s">
          <Box className="custom_tabs1" sx={{ width: "100%" }}>
            <Tabs
              value={valueTabs}
              onChange={handleChangeTabs}
              aria-label="basic tabs example"
              className="custom_tabs_links"
            >
              <Tab label="Rewards & Offers" {...a11yProps(0)} />
              {/* <Tab label="Referral Reward Earning" {...a11yProps(1)} /> */}
              <Tab label="Audit Log" {...a11yProps(1)} />
            </Tabs>
          </Box>
        </div>
      </div>

      <div className="cards">
        <CustomTabPanel value={valueTabs} index={0}>
          <div className="table_header">
            <div className="left_s">
              <SearchBar
                searchTerm={searchTerm}
                placeholder="Search..."
                setDebouncedSearchTerm={setDebouncedSearchTerm}
                value={searchTerm}
                onCross={() => setSearchTerm("")}
                onChange={(val: any) => {
                  if (isValidInput(val.target.value)) {
                    setSearchTerm(val.target.value);
                  }
                }}
              />
              <div className="form">
                <Select
                  fullWidth
                  value={TypeFilter}
                  onChange={(e: SelectChangeEvent) =>
                    setTypeFilter(e.target.value)
                  }
                >
                  <MenuItem disabled value="default">
                    Filter by Type
                  </MenuItem>
                  <MenuItem value="default">All Types</MenuItem>
                  <MenuItem value={`${REWARDSPROMOTIONS_TYPE.REWARDS}`}>
                    Offers & Rewards
                  </MenuItem>
                  <MenuItem value={`${REWARDSPROMOTIONS_TYPE.PROMOTIONS}`}>
                    Partner Engagement
                  </MenuItem>
                </Select>
                <Select
                  fullWidth
                  value={Status}
                  onChange={(e: SelectChangeEvent) => setStatus(e.target.value)}
                >
                  <MenuItem disabled value="default">
                    Select Visibility
                  </MenuItem>
                  <MenuItem value="default">All</MenuItem>
                  <MenuItem value="1">Enabled</MenuItem>
                  <MenuItem value="2">Disabled</MenuItem>
                </Select>
                <Select
                  fullWidth
                  value={Sort}
                  onChange={(e: SelectChangeEvent) => setSort(e.target.value)}
                >
                  <MenuItem disabled value="default">
                    Sort Order
                  </MenuItem>
                  <MenuItem value="default">Initial</MenuItem>
                  <MenuItem value="desc">Newest First</MenuItem>
                  <MenuItem value="asc">Oldest First</MenuItem>
                </Select>
              </div>
            </div>
            <div className="right_s">
              {isAdd && (
                <Button
                  className="btn btn_primary"
                  onClick={() => navigate("/manage-rewards/add")}
                >
                  <AddIcon /> Add Rewards & Offers
                </Button>
              )}
            </div>
          </div>
          <TableContainer className="table_container">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Reward Title</TableCell>
                  <TableCell>Reward Type</TableCell>
                  {/* <TableCell>Required Points</TableCell> */}
                  <TableCell>Validity Period</TableCell>
                  {/* <TableCell>Category</TableCell> */}
                  {/* <TableCell>Max Redemption</TableCell> */}
                  <TableCell>Created Date</TableCell>
                  <TableCell>Visibility</TableCell>
                  <TableCell>Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rewards.map((reward) => (
                  <TableRow hover key={reward.id}>
                    {/* Image */}
                    <TableCell>
                      <span
                        onClick={(e) => {
                          e.preventDefault();
                          isView &&
                            navigate(`/manage-rewards/detail/${reward?._id}`);
                        }}
                        className="user_block click_action"
                      >
                        <figure className="no_radius">
                          <img
                            src={
                              reward?.image || "/static/images/placeholder.png"
                            }
                            alt="Images"
                          />
                        </figure>
                        <p>
                          <b>{reward?.title || ""}</b>
                        </p>
                      </span>
                    </TableCell>

                    {/* Reward Type + Value */}
                    <TableCell>
                      <span className="status_btn c_info no_wrap">
                        {reward?.type === REWARD_TYPE.DISCOUNT
                          ? "Discount"
                          : "Cashback"}
                        :{" "}
                        {reward?.type === REWARD_TYPE.DISCOUNT
                          ? reward?.discountValue
                            ? `${reward?.discountValue}%`
                            : "-"
                          : reward?.cashbackValue || ""}
                      </span>
                    </TableCell>

                    {/* Loyalty Points */}
                    {/* <TableCell>{reward?.loyaltyPoints || '-'}</TableCell> */}

                    {/* Validity */}
                    <TableCell>
                      {reward?.startFrom || ""
                        ? new Date(reward?.startFrom || "").toLocaleDateString()
                        : "-"}{" "}
                      to{" "}
                      {reward?.expireAt || ""
                        ? new Date(reward?.expireAt || "").toLocaleDateString()
                        : "-"}
                    </TableCell>

                    {/* Category */}
                    {/* <TableCell>{reward?.category === REWARD_CATEGORY.BOOKS ? 'Books' :
                      reward?.category === REWARD_CATEGORY.STATIONARY ? 'Stationary' :
                        'Uniform'
                    }</TableCell> */}

                    {/* Max Redemption */}
                    {/* <TableCell>{reward?.maxRedemptions || ''}</TableCell> */}

                    {/* Created Date */}
                    <TableCell>
                      {reward?.createdAt
                        ? new Date(reward?.createdAt || "").toLocaleDateString()
                        : ""}
                    </TableCell>

                    {/* Visibility */}
                    <TableCell>
                      <SwitchToggle
                        checked={reward?.isActive}
                        onChange={(checked) => handleToggle(reward, checked)}
                      />
                    </TableCell>

                    {/* Actions */}
                    <TableCell>
                      <div className="table_actions v2">
                        {/* {isView && (
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-rewards/detail/${reward?._id}`)
                            }
                          >
                            <img src="/static/images/eye_icon.svg" alt="" />
                          </IconButton>
                        )} */}
                        {isEdit && (
                          <IconButton
                            onClick={() =>
                              navigate(`/manage-rewards/edit/${reward?._id}`)
                            }
                          >
                            <img src="/static/images/edit2_icon.svg" alt="" />
                          </IconButton>
                        )}
                        {isDelete && (
                          <IconButton
                            onClick={() => {
                              setActiveId(reward?._id || "");
                              toggleDeleteModal();
                            }}
                          >
                            <img src="/static/images/trash2_icon.svg" alt="" />
                          </IconButton>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <Pagination
            module={rewards || []}
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            onPageChange={onPageChange}
            totalPages={totalPages}
          />
        </CustomTabPanel>
        {/* <CustomTabPanel value={valueTabs} index={1}>
          <form className="form" action="">
            <div className="section_title">
              <h2>Reward Eligibility Criteria</h2>
            </div>
            <div className="gap_p">
              <div className="control_group w_100">
                <RadioGroup
                  className="flex"
                  aria-labelledby="demo-radio-buttons-group-label"
                  defaultValue="registration"
                  name="radio-buttons-group"
                >
                  <FormControlLabel value="registration" control={<Radio />} label="By referred user registration" />
                  <FormControlLabel value="purchase" control={<Radio />} label="By the first purchase of the referred user" />
                </RadioGroup>
              </div>
            </div>
            <div className="form_btn">
              <Button className="btn btn_primary">Submit</Button>
            </div>
          </form>
        </CustomTabPanel> */}
        <CustomTabPanel value={valueTabs} index={1}>
          <RewardAuditLogTable />
        </CustomTabPanel>
      </div>
      <DeleteConfirmationModal
        open={openDelete}
        onClose={toggleDeleteModal}
        handleDelete={handleDelete}
        header="Are you sure you want to delete this reward?"
      />
    </>
  );
};

export default ManageRewards;
